import React, { useContext, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ThumbsUp, MessageCircle } from "lucide-react";
import { UserContext } from "../../../UserContext";
import {
  fetchBlogById,
  fetchComments,
  likeBlog,
  postComment,
} from "../../../services/blogService";
import { getBlogId } from "../../../utils/blogUtils";

const fallbackURL =
  "https://media.istockphoto.com/id/2174551157/photo/cyber-security-data-protection-business-technology-privacy-concept.jpg?s=2048x2048&w=is&k=20&c=sCjAoM5vMvI7PXGFaTaOnRr1AppyOWmxPHkckruBq_A=";

const UserBlogDetails = () => {
  const { id } = useParams();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const blogData = location.state?.blog || null;

  const [blog, setBlog] = useState(blogData);
  const [liked, setLiked] = useState(
    localStorage.getItem(`liked-${id}`) === "true"
  );
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState([]);
  const [likes, setLikes] = useState(blogData?.like_count || 0);
  const [isLoading, setIsLoading] = useState(!blogData);

  const saveUserIntent = (actionType, blogId, payload = null) => {
    localStorage.setItem(
      "userIntent",
      JSON.stringify({
        actionType,
        blogId,
        payload,
        timestamp: Date.now(),
      })
    );
  };

  const redirectToAuth = useCallback(
    (intentType, payload = null) => {
      saveUserIntent(intentType, id, payload);
      navigate(`/signUp?redirect=${encodeURIComponent(location.pathname)}`);
    },
    [id, location.pathname, navigate]
  );

  const handleLike = useCallback(async () => {
    if (!blog) return;

    if (!user?.user_id) {
      redirectToAuth("like");
      return;
    }

    if (liked) return;

    try {
      await likeBlog(getBlogId(blog), user.access_token);
      setLikes((prev) => prev + 1);
      setLiked(true);
      localStorage.setItem(`liked-${getBlogId(blog)}`, "true");
    } catch (err) {
      console.error("Error liking blog:", err);
    }
  }, [blog, liked, redirectToAuth, user?.access_token, user?.user_id]);

  const handleAddComment = useCallback(
    async (commentOverride = null) => {
      if (!blog) return;

      const content = (commentOverride ?? commentText).trim();
      if (!content) return;

      if (!user?.user_id) {
        redirectToAuth("comment", content);
        return;
      }

      const tempId = `temp-${Date.now()}`;
      const newComment = {
        comment_id: tempId,
        user_id: user.user_id,
        text: content,
        username: [user.firstName, user.lastName].filter(Boolean).join(" "),
        blog_id: getBlogId(blog),
        created_at: new Date().toISOString(),
      };

      setComments((prev) => [newComment, ...prev]);
      setCommentText("");

      try {
        const savedComment = await postComment(getBlogId(blog), {
          text: content,
        }, user.access_token);

        setComments((prev) =>
          prev.map((comment) =>
            comment.comment_id === tempId ? savedComment : comment
          )
        );
      } catch (err) {
        console.error("Error posting comment:", err);
        setComments((prev) =>
          prev.filter((comment) => comment.comment_id !== tempId)
        );
      }
    },
    [blog, commentText, redirectToAuth, user]
  );

  useEffect(() => {
    if (blogData) {
      setBlog(blogData);
      setLikes(blogData.like_count || 0);
      setIsLoading(false);
      return;
    }

    const loadBlog = async () => {
      setIsLoading(true);
      try {
        const data = await fetchBlogById(id);
        setBlog(data);
        setLikes(data.like_count || 0);
      } catch (err) {
        console.error("Failed to fetch blog:", err);
        navigate("/blogs");
      } finally {
        setIsLoading(false);
      }
    };

    loadBlog();
  }, [blogData, id, navigate]);

  useEffect(() => {
    if (!blog) return;

    window.scrollTo(0, 0);

    const loadComments = async () => {
      try {
        const data = await fetchComments(getBlogId(blog));
        const sortedComments = (data ?? []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setLikes(blog.like_count || 0);
        setComments(sortedComments);
      } catch (err) {
        console.error("Error fetching comments:", err);
      }
    };

    loadComments();
  }, [blog]);

  useEffect(() => {
    const intent = JSON.parse(localStorage.getItem("userIntent"));

    if (
      blog &&
      intent &&
      intent.blogId === id &&
      Date.now() - intent.timestamp < 5 * 60 * 1000
    ) {
      const executeIntent = async () => {
        if (intent.actionType === "like" && !liked) {
          await handleLike();
        } else if (intent.actionType === "comment" && intent.payload) {
          await handleAddComment(intent.payload);
        }
        localStorage.removeItem("userIntent");
      };

      executeIntent();
    }
  }, [blog, id, liked, handleLike, handleAddComment]);

  if (isLoading) {
    return <p>Loading blog...</p>;
  }

  if (!blog) {
    return <p>Blog data not available. Please go back and try again.</p>;
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow rounded-xl mt-8">
      <img
        src={blog.image_url || fallbackURL}
        alt="Blog"
        className="w-full h-[20%] object-cover"
      />
      <h2 className="text-2xl font-bold my-2">{blog.title}</h2>

      <hr />
      <div className="flex justify-between text-xs text-gray-500 mt-2">
        <div className="flex items-center mb-2">
          <img
            src={`https://ui-avatars.com/api/?name=${blog.username}`}
            alt={blog.username}
            className="w-7 h-7 rounded-full mr-2"
          />
          <span className="text-sm text-black font-medium">
            {blog.username}
          </span>
        </div>
        <span className="mt-1">
          {new Date(blog.created_at).toLocaleDateString()}
        </span>
      </div>

      <hr />

      <div
        className="prose max-w-none text-gray-700 mt-2"
        dangerouslySetInnerHTML={{ __html: blog.description }}
      />

      {blog.blog_tags && (
        <div className="mt-6 flex flex-wrap gap-2">
          {blog.blog_tags.split(",").map((tag, index) => (
            <span
              key={`${getBlogId(blog)}-${index}`}
              className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
            >
              {tag.trim()}
            </span>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center text-sm text-gray-500 mt-6">
        <span>By: {blog.username}</span>
        <span>{new Date(blog.created_at).toLocaleDateString()}</span>
      </div>

      <hr className="mt-3" />
      <div className="mt-3">
        <div className="flex items-center gap-4 text-gray-600">
          <button
            className={`flex items-center gap-1 ${
              liked ? "text-blue-600" : "hover:text-blue-600"
            }`}
            onClick={handleLike}
          >
            <ThumbsUp className="w-4 h-4" />
            {likes}
          </button>
          <div className="flex items-center gap-1">
            <MessageCircle className="w-4 h-4" />
            {comments.length}
          </div>
        </div>

        <div className="mt-4">
          <textarea
            className="w-full border border-gray-300 p-2 rounded"
            placeholder="Add a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
          <button
            onClick={() => handleAddComment()}
            className="mt-2 px-4 py-1 bg-blue-600 text-white rounded"
          >
            Post Comment
          </button>
        </div>

        {comments.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold">Comments:</h4>
            {comments.map((comment) => (
              <div
                key={comment.comment_id}
                className="border border-gray-200 p-3 rounded mt-2"
              >
                <p className="text-sm font-medium text-gray-800">
                  {comment.username}
                </p>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-700">{comment.text}</p>
                  <span className="text-xs text-gray-500 ml-4">
                    {new Date(comment.created_at).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserBlogDetails;
